import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  RightOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import "./exam.css";

const { Title, Text } = Typography;
const { Dragger } = Upload;

const EXAM_API_BASE =
  process.env.REACT_APP_EXAM_API_BASE_URL ||
  "http://localhost:5000/api/exams";

const REQUIRED_HEADERS = [
  "question_no",
  "department_id",
  "level",
  "question_type",
  "question",
  "option_a",
  "option_b",
  "option_c",
  "option_d",
  "option_e",
  "correct_answer",
];

const ALLOWED_TYPES = new Set([
  "MCQ",
  "TRUE_FALSE",
  "SCENARIO",
]);

const ALLOWED_ANSWERS = new Set([
  "A",
  "B",
  "C",
  "D",
  "E",
]);

const ALLOWED_LEVELS = new Set([
  "level_1",
  "level_2",
  "level_3",
]);

const MAX_FILE_SIZE =
  5 * 1024 * 1024;

const MAX_ROWS = 1000;

const normalizeType = (value) => {
  const v = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

  if (
    [
      "TRUE/FALSE",
      "TRUE_FALSE",
      "TRUEFALSE",
    ].includes(v)
  ) {
    return "TRUE_FALSE";
  }

  if (
    [
      "SCENARIO",
      "SCENARIO_BASED",
    ].includes(v)
  ) {
    return "SCENARIO";
  }

  return v;
};

const cleanText = (value) =>
  String(value ?? "")
    .normalize("NFC")
    .trim();

const normalizeLevel = (value) =>
  cleanText(value)
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const positiveInt = (value) => {
  const n = Number(value);

  return (
    Number.isInteger(n) &&
    n > 0
  )
    ? n
    : null;
};

const generateExamName = () => {
  const now = new Date();

  const pad = (value) =>
    String(value)
      .padStart(2, "0");

  return (
    "Assessment-" +
    now.getFullYear() +
    pad(now.getMonth() + 1) +
    pad(now.getDate()) +
    "-" +
    pad(now.getHours()) +
    pad(now.getMinutes()) +
    pad(now.getSeconds())
  );
};

const parseJsonSafely =
  async (response) => {
    const raw =
      await response.text();

    if (!raw) {
      return {
        status:
          response.ok,
        message:
          response.ok
            ? "Request completed"
            : "Request failed",
      };
    }

    try {
      return JSON.parse(raw);
    } catch {
      return {
        status: false,
        message:
          `Server returned an invalid response (${response.status}).`,
        raw,
      };
    }
  };

export default function ExamUploadPage() {
  const [
    examName,
    setExamName,
  ] = useState("");

  const [
    durationMinutes,
    setDurationMinutes,
  ] = useState(30);

  const [
    selectedFile,
    setSelectedFile,
  ] = useState(null);

  const [
    previewRows,
    setPreviewRows,
  ] = useState([]);

  const [
    validationErrors,
    setValidationErrors,
  ] = useState([]);

  const [
    serverErrors,
    setServerErrors,
  ] = useState([]);

  const [
    missingHeaders,
    setMissingHeaders,
  ] = useState([]);

  const [
    parsing,
    setParsing,
  ] = useState(false);

  const [
    uploading,
    setUploading,
  ] = useState(false);

  const [
    page,
    setPage,
  ] = useState(1);

  const [
    pageSize,
    setPageSize,
  ] = useState(10);

  const departmentSummary =
    useMemo(() => {
      const map =
        new Map();

      previewRows.forEach(
        (row) => {
          const dept =
            positiveInt(
              row.department_id
            );
          const level = normalizeLevel(row.level);

          if (!dept || !ALLOWED_LEVELS.has(level)) return;

          const key = `${dept}:${level}`;

          map.set(
            key,
            {
              department_id: dept,
              level,
              question_count: (map.get(key)?.question_count || 0) + 1,
            }
          );
        }
      );

      return Array.from(
        map.entries()
      )
        .map(([, group]) => group)
        .sort(
          (a, b) => a.department_id - b.department_id || a.level.localeCompare(b.level)
        );
    }, [previewRows]);

  const errorRowSet =
    useMemo(
      () =>
        new Set(
          validationErrors
            .map(
              (item) =>
                Number(
                  item.row
                )
            )
            .filter(Boolean)
        ),
      [validationErrors]
    );

  const validateRows =
    (
      rows,
      headers
    ) => {
      const errors = [];

      const missing =
        REQUIRED_HEADERS.filter(
          (header) =>
            !headers.includes(
              header
            )
        );

      if (missing.length) {
        return {
          errors: [],
          missing,
        };
      }

      if (!rows.length) {
        errors.push({
          row: 2,
          field: "file",
          message:
            "Excel contains no question rows",
        });

        return {
          errors,
          missing: [],
        };
      }

      if (
        rows.length >
        MAX_ROWS
      ) {
        errors.push({
          row: 2,
          field: "file",
          message:
            `Maximum ${MAX_ROWS} questions are allowed per upload`,
        });
      }

      rows.forEach(
        (
          row,
          index
        ) => {
          const excelRow =
            index + 2;

          const departmentId =
            positiveInt(
              row.department_id
            );

          const level = normalizeLevel(row.level);

          const questionType =
            normalizeType(
              row.question_type
            );

          const question =
            cleanText(
              row.question
            );

          let optionA =
            cleanText(
              row.option_a
            );

          let optionB =
            cleanText(
              row.option_b
            );

          let optionC =
            cleanText(
              row.option_c
            );

          let optionD =
            cleanText(
              row.option_d
            );

          let optionE =
            cleanText(
              row.option_e
            );

          let correctAnswer =
            cleanText(
              row.correct_answer
            ).toUpperCase();

          if (!departmentId) {
            errors.push({
              row:
                excelRow,
              field:
                "department_id",
              message:
                "Valid positive department_id is required",
            });
          }

          if (!ALLOWED_LEVELS.has(level)) {
            errors.push({
              row: excelRow,
              field: "level",
              message: "Use level_1, level_2, or level_3",
            });
          }

          if (
            !ALLOWED_TYPES.has(
              questionType
            )
          ) {
            errors.push({
              row:
                excelRow,
              field:
                "question_type",
              message:
                "Use MCQ, TRUE_FALSE, or SCENARIO",
            });
          }

          if (
            !question ||
            question.length < 3
          ) {
            errors.push({
              row:
                excelRow,
              field:
                "question",
              message:
                "Question is required and must contain at least 3 characters",
            });
          }

          if (
            questionType ===
            "TRUE_FALSE"
          ) {
            optionA =
              optionA ||
              "True";

            optionB =
              optionB ||
              "False";

            optionC = "";
            optionD = "";
            optionE = "";

            if (
              correctAnswer ===
              "TRUE"
            ) {
              correctAnswer =
                "A";
            }

            if (
              correctAnswer ===
              "FALSE"
            ) {
              correctAnswer =
                "B";
            }

            if (
              ![
                "A",
                "B",
              ].includes(
                correctAnswer
              )
            ) {
              errors.push({
                row:
                  excelRow,
                field:
                  "correct_answer",
                message:
                  "TRUE_FALSE answer must be A/B or True/False",
              });
            }
          } else if (
            !optionA ||
            !optionB
          ) {
            errors.push({
              row:
                excelRow,
              field:
                "options",
              message:
                "At least option_a and option_b are required",
            });
          }

          if (
            !ALLOWED_ANSWERS.has(
              correctAnswer
            )
          ) {
            errors.push({
              row:
                excelRow,
              field:
                "correct_answer",
              message:
                "correct_answer must be A, B, C, D, or E",
            });
          }

          const options = {
            A: optionA,
            B: optionB,
            C: optionC,
            D: optionD,
            E: optionE,
          };

          if (
            ALLOWED_ANSWERS.has(
              correctAnswer
            ) &&
            !options[
              correctAnswer
            ]
          ) {
            errors.push({
              row:
                excelRow,
              field:
                "correct_answer",
              message:
                `Correct answer ${correctAnswer} has no matching option value`,
            });
          }
        }
      );

      return {
        errors,
        missing: [],
      };
    };

const downloadTemplate = () => {
  const workbook = XLSX.utils.book_new();
  const questionSheet = XLSX.utils.aoa_to_sheet([
    REQUIRED_HEADERS,
  ]);
  questionSheet["!cols"] = [
    { wch: 14 },
    { wch: 16 },
    { wch: 14 },
    { wch: 18 },
    { wch: 55 },
    { wch: 38 },
    { wch: 38 },
    { wch: 38 },
    { wch: 38 },
    { wch: 38 },
    { wch: 18 },
  ];

  const instructionsSheet = XLSX.utils.aoa_to_sheet([
    ["Exam question upload template"],
    ["Fill one row per question in the Questions sheet. Do not rename the headers."],
    ["level", "Required. Use level_1, level_2, or level_3."],
    ["department_id", "Required numeric department ID."],
    ["question_type", "Use MCQ, TRUE_FALSE, or SCENARIO."],
    ["question / option_a ... option_e", "Enter English, Kannada, and Hindi text separated by commas, matching the existing multilingual format."],
    ["correct_answer", "Use A, B, C, D, or E. TRUE_FALSE also accepts TRUE or FALSE."],
    ["question_no", "Question order within the department and level."],
    ["Marks", "Each question is currently saved as 1 mark."],
    ["Upload behavior", "A separate active exam is created for each department and level."],
  ]);
  instructionsSheet["!cols"] = [{ wch: 32 }, { wch: 105 }];

  XLSX.utils.book_append_sheet(workbook, questionSheet, "Questions");
  XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");
  XLSX.writeFile(workbook, "exam_questions_level_template.xlsx");
};

  const parseExcel =
    async (file) => {
      try {
        setParsing(true);
        setServerErrors([]);
        setValidationErrors([]);
        setMissingHeaders([]);
        setPreviewRows([]);
        setPage(1);

        const extension =
          String(
            file.name || ""
          )
            .toLowerCase();

        if (
          !extension.endsWith(
            ".xlsx"
          ) &&
          !extension.endsWith(
            ".xls"
          )
        ) {
          message.error(
            "Only .xlsx or .xls files are allowed."
          );

          return false;
        }

        if (
          file.size >
          MAX_FILE_SIZE
        ) {
          message.error(
            "Excel file must be 5 MB or smaller."
          );

          return false;
        }

        const buffer =
          await file.arrayBuffer();

        let workbook;

        try {
          workbook =
            XLSX.read(
              buffer,
              {
                type: "array",
                cellDates: false,
              }
            );
        } catch (error) {
          console.error(
            "Excel parse error:",
            error
          );

          message.error(
            "Unable to read the Excel file."
          );

          return false;
        }

        const sheetName =
          workbook
            .SheetNames?.[0];

        if (!sheetName) {
          message.error(
            "Excel file does not contain a worksheet."
          );

          return false;
        }

        const sheet =
          workbook
            .Sheets[
              sheetName
            ];

        const matrix =
          XLSX.utils
            .sheet_to_json(
              sheet,
              {
                header: 1,
                defval: "",
                raw: false,
              }
            );

        if (
          !matrix.length
        ) {
          message.error(
            "Excel sheet is empty."
          );

          return false;
        }

        const headers =
          (matrix[0] || [])
            .map(
              (header) =>
                cleanText(
                  header
                ).toLowerCase()
            );

        const rows =
          XLSX.utils
            .sheet_to_json(
              sheet,
              {
                defval: "",
                raw: false,
              }
            )
            .map(
              (row) => {
                const normalized =
                  {};

                Object.entries(
                  row
                ).forEach(
                  ([
                    key,
                    value,
                  ]) => {
                    normalized[
                      cleanText(
                        key
                      ).toLowerCase()
                    ] =
                      value;
                  }
                );

                return normalized;
              }
            );

        const validation =
          validateRows(
            rows,
            headers
          );

        setSelectedFile(
          file
        );

        setPreviewRows(
          rows.map(
            (
              row,
              index
            ) => ({
              ...row,
              __key:
                index + 2,
              __excelRow:
                index + 2,
            })
          )
        );

        setValidationErrors(
          validation.errors
        );

        setMissingHeaders(
          validation.missing
        );

        if (
          validation.missing
            .length
        ) {
          message.error(
            "Excel columns are not in the required format."
          );
        } else if (
          validation.errors
            .length
        ) {
          message.warning(
            `Excel loaded with ${validation.errors.length} validation issue(s).`
          );
        } else {
          message.success(
            `Excel validated successfully. ${rows.length} question(s) found.`
          );
        }

        return false;
      } catch (error) {
        console.error(
          "Excel preview error:",
          error
        );

        message.error(
          "Unable to preview the Excel file."
        );

        return false;
      } finally {
        setParsing(false);
      }
    };

  const resetForm = () => {
    setExamName("");
    setDurationMinutes(30);
    setSelectedFile(null);
    setPreviewRows([]);
    setValidationErrors([]);
    setServerErrors([]);
    setMissingHeaders([]);
    setPage(1);
  };

  const showServerValidation =
    (errors = []) => {
      setServerErrors(
        Array.isArray(errors)
          ? errors
          : []
      );

      if (
        Array.isArray(errors) &&
        errors.length
      ) {
        Modal.error({
          rootClassName: "exam-upload-modal-root",
          title:
            "Excel Validation Failed",
          width: 680,
          content: (
            <div className="exam-upload-server-error-list">
              {errors
                .slice(0, 20)
                .map(
                  (
                    item,
                    index
                  ) => (
                    <div
                      key={
                        index
                      }
                    >
                      <strong>
                        Row{" "}
                        {item.row ??
                          "-"}
                      </strong>
                      {" — "}
                      {item.field
                        ? `${item.field}: `
                        : ""}
                      {item.message ||
                        "Validation error"}
                    </div>
                  )
                )}

              {errors.length >
                20 && (
                <div className="exam-upload-more-errors">
                  +{" "}
                  {errors.length -
                    20}{" "}
                  more issue(s)
                </div>
              )}
            </div>
          ),
        });
      }
    };

  const handleUpload =
    async () => {
      if (
        !selectedFile
      ) {
        message.error(
          "Please select an Excel file."
        );

        return;
      }

      if (
        missingHeaders.length
      ) {
        message.error(
          "Please fix the missing Excel columns before uploading."
        );

        return;
      }

      if (
        validationErrors.length
      ) {
        message.error(
          "Please fix all Excel validation errors before uploading."
        );

        return;
      }

      const duration =
        Number(
          durationMinutes
        );

      if (
        !Number.isInteger(
          duration
        ) ||
        duration < 1 ||
        duration > 1440
      ) {
        message.error(
          "Duration must be between 1 and 1440 minutes."
        );

        return;
      }

      const finalExamName =
        cleanText(
          examName
        ) ||
        generateExamName();

      if (
        finalExamName.length >
        200
      ) {
        message.error(
          "Exam name cannot exceed 200 characters."
        );

        return;
      }

      try {
        setUploading(true);
        setServerErrors([]);

        const formData =
          new FormData();

        formData.append(
          "exam_name",
          finalExamName
        );

        formData.append(
          "duration_minutes",
          String(duration)
        );

        formData.append(
          "file",
          selectedFile
        );

        const response =
          await fetch(
            `${EXAM_API_BASE}/upload`,
            {
              method:
                "POST",
              body:
                formData,
            }
          );

        const result =
          await parseJsonSafely(
            response
          );

        if (
          !response.ok ||
          !result?.status
        ) {
          const errorMessage =
            result?.message ||
            `Upload failed (${response.status})`;

          if (
            Array.isArray(
              result?.errors
            ) &&
            result.errors
              .length
          ) {
            showServerValidation(
              result.errors
            );
          }

          message.error(
            errorMessage
          );

          return;
        }

        const createdExams =
          Array.isArray(
            result?.data
              ?.exams
          )
            ? result.data
                .exams
            : result?.data
              ? [
                  result.data,
                ]
              : [];

        const createdCount =
          result?.data
            ?.exam_count ||
          createdExams.length ||
          1;

        message.success(
          result?.message ||
            `${createdCount} exam(s) created successfully.`
        );

        Modal.success({
          rootClassName: "exam-upload-modal-root",
          title:
            "Exam Upload Successful",
          width: 640,
          content: (
            <div className="exam-upload-success-summary">
              <p>
                <strong>
                  Exam Name:
                </strong>{" "}
                {finalExamName}
              </p>

              <p>
                <strong>
                  Duration:
                </strong>{" "}
                {duration} minutes
              </p>

              <p>
                <strong>
                  Questions:
                </strong>{" "}
                {previewRows.length}
              </p>

              <p>
                <strong>
                  Department-level exams:
                </strong>{" "}
                {createdCount}
              </p>

              {createdExams.length >
                0 && (
                <div className="exam-upload-created-list">
                  {createdExams.map(
                    (
                      item,
                      index
                    ) => (
                      <div
                        key={
                          item.exam_id ||
                          index
                        }
                      >
                        Dept{" "}
                        {
                          item.department_id
                        }
                        {" — "}
                        {item.level}
                        {" — "}
                        {
                          item.question_count
                        }{" "}
                        question(s)
                        {item.exam_id
                          ? ` — Exam #${item.exam_id}`
                          : ""}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          ),
          onOk:
            resetForm,
        });

        if (
          !cleanText(
            examName
          )
        ) {
          setExamName(
            finalExamName
          );
        }
      } catch (error) {
        console.error(
          "Exam upload error:",
          error
        );

        message.error(
          "Unable to connect to the exam server. Please try again."
        );
      } finally {
        setUploading(false);
      }
    };

  const previewColumns = [
    {
      title: "Row",
      dataIndex:
        "__excelRow",
      width: 68,
      fixed: "left",
      render: (
        value,
        record
      ) => (
        <span
          className={
            errorRowSet.has(
              Number(
                record.__excelRow
              )
            )
              ? "exam-upload-row-error"
              : ""
          }
        >
          {value}
        </span>
      ),
    },
    {
      title: "Q No.",
      dataIndex:
        "question_no",
      width: 76,
    },
    {
      title: "Dept",
      dataIndex:
        "department_id",
      width: 82,
      render: (value) =>
        value || "-",
    },
    {
      title: "Level",
      dataIndex: "level",
      width: 100,
      render: (value) => value || "-",
    },
    {
      title: "Type",
      dataIndex:
        "question_type",
      width: 118,
      render: (value) => {
        const normalized =
          normalizeType(
            value
          );

        return (
          <Tag>
            {normalized ||
              "-"}
          </Tag>
        );
      },
    },
    {
      title: "Question",
      dataIndex:
        "question",
      width: 340,
      render: (value) => (
        <div className="exam-upload-question-cell">
          {value || "-"}
        </div>
      ),
    },
    {
      title: "A",
      dataIndex:
        "option_a",
      width: 180,
      ellipsis: true,
    },
    {
      title: "B",
      dataIndex:
        "option_b",
      width: 180,
      ellipsis: true,
    },
    {
      title: "C",
      dataIndex:
        "option_c",
      width: 180,
      ellipsis: true,
    },
    {
      title: "D",
      dataIndex:
        "option_d",
      width: 180,
      ellipsis: true,
    },
    {
      title: "E",
      dataIndex:
        "option_e",
      width: 180,
      ellipsis: true,
    },
    {
      title:
        "Answer",
      dataIndex:
        "correct_answer",
      width: 92,
      fixed: "right",
      render: (value) => (
        <strong>
          {value || "-"}
        </strong>
      ),
    },
  ];

  const visibleRows =
    useMemo(() => {
      const start =
        (page - 1) *
        pageSize;

      return previewRows.slice(
        start,
        start +
          pageSize
      );
    }, [
      previewRows,
      page,
      pageSize,
    ]);

  const renderPreviewValue = (value) => {
    const text = cleanText(value);

    return text || "-";
  };

  const renderMobilePreviewCard = (record) => {
    const hasError =
      errorRowSet.has(
        Number(
          record.__excelRow
        )
      );

    return (
      <div
        key={record.__key}
        className={
          hasError
            ? "exam-upload-mobile-preview-card exam-upload-mobile-preview-card-error"
            : "exam-upload-mobile-preview-card"
        }
      >
        <div className="exam-upload-mobile-preview-head">
          <div>
            <span>Row</span>
            <strong>
              {record.__excelRow}
            </strong>
          </div>

          <div>
            <span>Q No.</span>
            <strong>
              {renderPreviewValue(
                record.question_no
              )}
            </strong>
          </div>

          <div>
            <span>Dept</span>
            <strong>
              {renderPreviewValue(
                record.department_id
              )}
            </strong>
          </div>

          <div>
            <span>Level</span>
            <strong>
              {renderPreviewValue(record.level)}
            </strong>
          </div>

          <Tag>
            {normalizeType(
              record.question_type
            ) || "-"}
          </Tag>
        </div>

        <div className="exam-upload-mobile-preview-section">
          <span>Question</span>

          <p>
            {renderPreviewValue(
              record.question
            )}
          </p>
        </div>

        <div className="exam-upload-mobile-options">
          {[
            ["A", record.option_a],
            ["B", record.option_b],
            ["C", record.option_c],
            ["D", record.option_d],
            ["E", record.option_e],
          ].map(([key, value]) => {
            const text =
              cleanText(value);

            if (!text) {
              return null;
            }

            const isAnswer =
              cleanText(
                record.correct_answer
              ).toUpperCase() ===
              key;

            return (
              <div
                key={key}
                className={
                  isAnswer
                    ? "exam-upload-mobile-option exam-upload-mobile-option-answer"
                    : "exam-upload-mobile-option"
                }
              >
                <strong>
                  {key}
                </strong>

                <span>
                  {text}
                </span>

                {isAnswer && (
                  <em>
                    Correct
                  </em>
                )}
              </div>
            );
          })}
        </div>

        {hasError && (
          <div className="exam-upload-mobile-preview-error">
            This row has validation issue(s).
          </div>
        )}
      </div>
    );
  };

  const canUpload =
    selectedFile &&
    !parsing &&
    !uploading &&
    !missingHeaders.length &&
    !validationErrors.length &&
    previewRows.length > 0;

  return (
    <div className="exam-management-page exam-management-upload-page">
      <section className="exam-management-hero exam-management-upload-hero">
        <div className="exam-management-hero-pattern" />

        <div className="exam-management-hero-content">
          <div className="exam-management-hero-copy">
            <div className="exam-management-breadcrumb">
              <span className="exam-management-breadcrumb-home">
                <HomeOutlined />
                <span>SM-ART</span>
              </span>

              <RightOutlined />

              <span>Exam Management</span>

              <RightOutlined />

              <strong>Upload Exam Excel</strong>
            </div>

            <Title level={3}>Upload Exam Excel</Title>

            <Text>
              Upload, validate, preview, and create department-wise assessments.
            </Text>
          </div>

          <Button
            icon={<DownloadOutlined />}
            onClick={downloadTemplate}
            className="exam-outline-button"
          >
            Download Excel Template
          </Button>
        </div>
      </section>

      <div className="exam-management-content-panel exam-management-upload-panel">
      <Card className="exam-upload-form-card exam-management-surface">
        <Row gutter={[18, 18]}>
          <Col
            xs={24}
            md={16}
          >
            <label className="exam-upload-label">
              Exam Name
              <span>
                Optional
              </span>
            </label>

            <Input
              value={
                examName
              }
              maxLength={
                200
              }
              disabled={
                uploading
              }
              placeholder="Leave blank to generate automatically"
              onChange={(
                event
              ) =>
                setExamName(
                  event.target
                    .value
                )
              }
            />

            <div className="exam-upload-help">
              If blank, the system will generate a unique assessment name during upload.
            </div>
          </Col>

          <Col
            xs={24}
            md={8}
          >
            <label className="exam-upload-label">
              Duration
              <span>
                Minutes
              </span>
            </label>

            <InputNumber
              value={
                durationMinutes
              }
              min={1}
              max={1440}
              precision={0}
              disabled={
                uploading
              }
              onChange={
                setDurationMinutes
              }
              style={{
                width:
                  "100%",
              }}
            />

            <div className="exam-upload-help">
              Allowed range: 1–1440 minutes.
            </div>
          </Col>
        </Row>

        <Divider />

        <label className="exam-upload-label">
          Excel File
          <span>
            .xlsx / .xls • max 5 MB
          </span>
        </label>

        <Dragger
          accept=".xlsx,.xls"
          multiple={false}
          maxCount={1}
          fileList={
            selectedFile
              ? [
                  {
                    uid: "-1",
                    name:
                      selectedFile.name,
                    status:
                      "done",
                  },
                ]
              : []
          }
          beforeUpload={
            parseExcel
          }
          onRemove={() => {
            setSelectedFile(
              null
            );
            setPreviewRows(
              []
            );
            setValidationErrors(
              []
            );
            setServerErrors(
              []
            );
            setMissingHeaders(
              []
            );

            return true;
          }}
          disabled={
            parsing ||
            uploading
          }
          className="exam-upload-dragger"
        >
          {parsing ? (
            <>
              <Spin />
              <p className="ant-upload-text">
                Reading Excel...
              </p>
            </>
          ) : (
            <>
              <p className="ant-upload-drag-icon">
                <CloudUploadOutlined />
              </p>

              <p className="ant-upload-text">
                Click or drag Excel file here
              </p>

              <p className="ant-upload-hint">
                The file is validated locally before it can be uploaded.
              </p>
            </>
          )}
        </Dragger>
      </Card>

      {missingHeaders.length >
        0 && (
        <Alert
          className="exam-upload-alert"
          type="error"
          showIcon
          message="Required Excel columns are missing"
          description={
            <div>
              Missing:{" "}
              <strong>
                {missingHeaders.join(
                  ", "
                )}
              </strong>

              <div className="exam-upload-required-columns">
                Required columns:{" "}
                {REQUIRED_HEADERS.join(
                  ", "
                )}
              </div>
            </div>
          }
        />
      )}

      {previewRows.length >
        0 && (
        <>
          <div className="exam-upload-summary-grid">
            <div>
              <span>
                Questions
              </span>
              <strong>
                {
                  previewRows.length
                }
              </strong>
            </div>

            <div>
              <span>
                Departments
              </span>
              <strong>
                {new Set(departmentSummary.map((item) => item.department_id)).size}
              </strong>
            </div>

            <div>
              <span>
                Validation
              </span>
              <strong
                className={
                  validationErrors.length
                    ? "exam-upload-summary-danger"
                    : "exam-upload-summary-success"
                }
              >
                {validationErrors.length
                  ? `${validationErrors.length} issue(s)`
                  : "Passed"}
              </strong>
            </div>

            <div>
              <span>
                Duration
              </span>
              <strong>
                {durationMinutes ||
                  "-"}{" "}
                min
              </strong>
            </div>
          </div>

          <Card
            className="exam-upload-department-card exam-management-surface"
            title="Department and Level Summary"
          >
            <Space
              size={[
                8,
                8,
              ]}
              wrap
            >
              {departmentSummary.map(
                (item) => (
                  <Tag
                    key={
                      item.department_id
                    }
                    className="exam-upload-department-tag"
                  >
                    Dept{" "}
                    {
                      item.department_id
                    }
                    {" • "}
                    {item.level}
                    {" • "}
                    {
                      item.question_count
                    }{" "}
                    question(s)
                  </Tag>
                )
              )}
            </Space>
          </Card>
        </>
      )}

      {validationErrors.length >
        0 && (
        <Card
          className="exam-upload-validation-card exam-management-surface"
          title={`Validation Issues (${validationErrors.length})`}
        >
          <div className="exam-upload-validation-list">
            {validationErrors
              .slice(0, 100)
              .map(
                (
                  error,
                  index
                ) => (
                  <div
                    className="exam-upload-validation-item"
                    key={`${error.row}-${error.field}-${index}`}
                  >
                    <strong>
                      Row{" "}
                      {error.row}
                    </strong>

                    <span>
                      {
                        error.field
                      }
                    </span>

                    <p>
                      {
                        error.message
                      }
                    </p>
                  </div>
                )
              )}
          </div>
        </Card>
      )}

      {serverErrors.length >
        0 && (
        <Alert
          className="exam-upload-alert"
          type="error"
          showIcon
          message="Server validation errors"
          description={`${serverErrors.length} server-side validation issue(s) were returned. Correct the Excel and upload again.`}
        />
      )}

      {previewRows.length >
        0 && (
        <Card
          className="exam-upload-preview-card exam-management-surface"
          title={
            <span>
              <FileExcelOutlined />{" "}
              Excel Preview
            </span>
          }
          extra={
            <span className="exam-upload-preview-note">
              Rows with validation problems are highlighted.
            </span>
          }
        >
          <div className="exam-upload-desktop-preview">
            <Table
              rowKey="__key"
              columns={
                previewColumns
              }
              dataSource={
                visibleRows
              }
              pagination={false}
              size="small"
              scroll={{
                x: "max-content",
              }}
              rowClassName={(
                record
              ) =>
                errorRowSet.has(
                  Number(
                    record.__excelRow
                  )
                )
                  ? "exam-upload-table-error-row"
                  : ""
              }
            />
          </div>

          <div className="exam-upload-mobile-preview">
            {visibleRows.map(
              renderMobilePreviewCard
            )}
          </div>

          <div className="exam-upload-pagination exam-upload-preview-pagination">
            <span>
              Showing{" "}
              {previewRows.length
                ? (page - 1) *
                    pageSize +
                  1
                : 0}
              –
              {Math.min(
                page *
                  pageSize,
                previewRows.length
              )}{" "}
              of{" "}
              {
                previewRows.length
              }
            </span>

            <Pagination
              responsive
              showLessItems
              current={
                page
              }
              pageSize={
                pageSize
              }
              total={
                previewRows.length
              }
              showSizeChanger
              pageSizeOptions={[
                10,
                20,
                50,
                100,
              ]}
              onChange={(
                nextPage,
                nextPageSize
              ) => {
                setPage(
                  nextPage
                );

                if (
                  nextPageSize !==
                  pageSize
                ) {
                  setPageSize(
                    nextPageSize
                  );
                  setPage(1);
                }
              }}
            />
          </div>
        </Card>
      )}

      <div className="exam-upload-actions">
        {selectedFile && (
          <Button
            icon={
              <DeleteOutlined />
            }
            disabled={
              uploading
            }
            onClick={
              resetForm
            }
            className="exam-upload-clear-button"
          >
            Clear
          </Button>
        )}

        <Button
          icon={<ReloadOutlined />}
          disabled={uploading}
          onClick={resetForm}
          className="exam-upload-reset-button"
        >
          Reset
        </Button>

        <Button
          type="primary"
          size="large"
          icon={
            <CheckCircleOutlined />
          }
          loading={
            uploading
          }
          disabled={
            !canUpload
          }
          onClick={
            handleUpload
          }
          className="exam-upload-primary-button"
        >
          {uploading
            ? "Creating Exam..."
            : "Create Exam"}
        </Button>
      </div>
      </div>
    </div>
  );
}
